import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, CheckCircle2, Briefcase, Coins,
  Layers, Globe2, User, Landmark, Building2, HardHat,
  Rocket, Scale, ShieldCheck,
} from "lucide-react";
import ClinqLogo from "../components/ClinqLogo";
import { FUNDER_QUIZ_STORAGE_KEY } from "../lib/funderQuizMandate";
import { trackPageView } from "../lib/tracker";

const SECTORS = [
  { id: "agriculture",  label: "Agriculture / AgriTech" },
  { id: "healthcare",   label: "Healthcare / HealthTech" },
  { id: "financial",    label: "Financial Services / FinTech" },
  { id: "manufacturing", label: "Manufacturing / Industrials" },
  { id: "retail",       label: "Retail / Consumer Goods" },
  { id: "logistics",    label: "Logistics & Supply Chain" },
  { id: "energy",       label: "Energy / CleanTech" },
  { id: "real_estate",  label: "Real Estate / Construction" },
  { id: "tech_saas",    label: "Technology / SaaS" },
  { id: "education",    label: "Education / EdTech" },
  { id: "hospitality",  label: "Hospitality / Tourism" },
  { id: "agnostic",     label: "Any Sector is OK" },
  { id: "other",        label: "Other" },
];

const TICKETS = [
  { id: "lt_50k",   label: "Less than USD 50K" },
  { id: "50k_250k", label: "USD 50K – 250K" },
  { id: "250k_1m",  label: "USD 250K – 1M" },
  { id: "1m_5m",    label: "USD 1M – 5M" },
  { id: "5m_15m",   label: "USD 5M – 15M" },
  { id: "gt_15m",   label: "Above USD 15M" },
];

const DEAL_TYPES = [
  { id: "businesses", Icon: Building2, label: "Businesses",
    hint: "Operating SMEs raising growth capital — fintech, agri, retail, manufacturing and more." },
  { id: "projects", Icon: HardHat, label: "Projects",
    hint: "e.g. real estate units, solar & energy plants, infrastructure, agri projects." },
  { id: "both", Icon: Layers, label: "Both",
    hint: "Show me businesses and projects that fit my mandate." },
];

const RISK_PROFILES = [
  { id: "bold", Icon: Rocket, label: "The Bold Builder",
    tag: "Higher risk, higher return",
    hint: "Early ventures with big upside — I'm comfortable taking losses along the way for outsized wins." },
  { id: "balanced", Icon: Scale, label: "The Balanced Investor",
    tag: "Medium risk",
    hint: "A mix of growth deals and structured, protected positions — upside with downside cover." },
  { id: "steady", Icon: ShieldCheck, label: "The Steady Earner",
    tag: "Lower risk",
    hint: "Secured, income-generating investments with predictable returns over speculative bets." },
];
const RISK_TO_STRUCTURES = {
  bold: ["equity", "convertible", "open"],
  balanced: ["equity", "debt", "mezzanine", "mix"],
  steady: ["debt", "mix"],
};

const GEOS = [
  { id: "kenya_only",  label: "Kenya only" },
  { id: "east_africa", label: "East Africa" },
  { id: "pan_african", label: "Pan-African" },
  { id: "specific",    label: "Specific countries" },
];

const INVESTOR_TYPES = [
  { id: "individual", Icon: User, label: "As an individual/group",
    hint: "Investing my own money or with a group (chama, investment club) — off-plan units, stakes in growing businesses, local projects." },
  { id: "institution", Icon: Landmark, label: "As an institution",
    hint: "A fund, bank, DFI, SACCO, family office or corporate deploying mandated capital." },
];

const STEP_META = [
  { Icon: User,       title: <>Are you investing as an <span className="text-emerald-400">individual/group</span> or as an <span className="text-emerald-400">institution</span>?</>, sub: "This helps us tailor the opportunities and reporting to how you invest." },
  { Icon: Briefcase,  title: <>Are there particular business <span className="text-emerald-400">sectors</span> you prefer to invest in?</>, sub: "Select all that apply — we'll surface SMEs in these sectors first." },
  { Icon: Coins,      title: <>How much are you looking to invest <span className="text-emerald-400">per business</span>?</>, sub: "In USD. This sets the ticket band we match you against." },
  { Icon: HardHat,    title: <>Are you interested in investing in a <span className="text-emerald-400">business</span> or a <span className="text-emerald-400">project</span>?</>, sub: "Projects include things like real estate units, solar plants, infrastructure and agri developments." },
  { Icon: Layers,     title: <>Which investor sounds most like <span className="text-emerald-400">you</span>?</>, sub: "This sets your risk appetite — it shapes which deals we surface first. You can refine it anytime." },
  { Icon: Globe2,     title: <>What is your <span className="text-emerald-400">geographic focus</span>?</>, sub: "Select all that apply." },
];

export default function GetStartedFunder() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  // Virtual step views power the Admin quiz drop-off report.
  useEffect(() => { trackPageView(`/get-started-funder/step-${step}`); }, [step]);
  const [sectors, setSectors] = useState([]);
  const [investorType, setInvestorType] = useState("");
  const [sectorOther, setSectorOther] = useState("");
  const [ticket, setTicket] = useState("");
  const [dealFocus, setDealFocus] = useState("");
  const [structures, setStructures] = useState([]);
  const [riskAppetite, setRiskAppetite] = useState("");
  const [geos, setGeos] = useState([]);
  const [geoSpecific, setGeoSpecific] = useState("");
  // Guard the persist effect until the restore effect has run, so a mount
  // can never clobber a saved draft with the initial empty state.
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(FUNDER_QUIZ_STORAGE_KEY);
      if (raw) {
        const o = JSON.parse(raw);
        if (Array.isArray(o?.sectors)) setSectors(o.sectors);
        if (typeof o?.investorType === "string") setInvestorType(o.investorType);
        if (typeof o?.sectorOther === "string") setSectorOther(o.sectorOther);
        if (o?.ticket) setTicket(o.ticket);
        if (typeof o?.dealFocus === "string") setDealFocus(o.dealFocus);
        if (Array.isArray(o?.structures)) setStructures(o.structures);
        if (typeof o?.riskAppetite === "string") setRiskAppetite(o.riskAppetite);
        if (Array.isArray(o?.geos)) setGeos(o.geos);
        if (typeof o?.geoSpecific === "string") setGeoSpecific(o.geoSpecific);
        // Resume where the funder left off (refresh-proof).
        const s = Number(o?.step);
        if (Number.isInteger(s) && s >= 2 && s <= 6 && o?.investorType) setStep(s);
      }
    } catch (_) { /* ignore */ }
    setHydrated(true);
  }, []);
  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(FUNDER_QUIZ_STORAGE_KEY, JSON.stringify({
        investorType, sectors, sectorOther, ticket, dealFocus, structures, riskAppetite, geos, geoSpecific, step,
      }));
    } catch (_) { /* ignore quota */ }
  }, [hydrated, investorType, sectors, sectorOther, ticket, dealFocus, structures, riskAppetite, geos, geoSpecific, step]);

  const toggle = (setter) => (id) => setter((curr) =>
    curr.includes(id) ? curr.filter((x) => x !== id) : [...curr, id]);

  const canNext = {
    1: !!investorType,
    2: sectors.length > 0 && (!sectors.includes("other") || sectorOther.trim().length > 0),
    3: !!ticket,
    4: !!dealFocus,
    5: !!riskAppetite,
    6: geos.length > 0 && (!geos.includes("specific") || geoSpecific.trim().length > 0),
  }[step];

  const proceed = () => {
    if (!canNext) return;
    if (step < 6) { setStep(step + 1); return; }
    try {
      localStorage.setItem(FUNDER_QUIZ_STORAGE_KEY, JSON.stringify({
        investorType, sectors, sectorOther, ticket, dealFocus, structures, riskAppetite, geos, geoSpecific, step,
        completed_at: new Date().toISOString(),
      }));
    } catch (_) { /* ignore */ }
    nav("/signup?role=funder");
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const MultiCard = ({ opt, active, onClick, testid }) => (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={
        "flex items-center gap-3 rounded-xl border px-4 py-3.5 text-left transition-all " +
        (active
          ? "border-[#F59E0B] bg-[#F59E0B]/10 ring-2 ring-[#F59E0B]/40"
          : "border-white/10 bg-white/[0.03] hover:border-emerald-400/50 hover:bg-white/[0.06]")
      }
    >
      <div className={
        "flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 transition-all " +
        (active ? "border-[#F59E0B] bg-[#F59E0B]" : "border-white/30 bg-transparent")
      }>
        {active && <CheckCircle2 className="h-4 w-4 text-[#04302E]" />}
      </div>
      <div className="min-w-0">
        <span className="text-sm font-semibold tracking-tight">{opt.label}</span>
        {opt.hint && <span className="block text-xs text-white/50">{opt.hint}</span>}
      </div>
    </button>
  );

  const meta = STEP_META[step - 1];
  const MetaIcon = meta.Icon;

  return (
    <div className="min-h-screen bg-[#04302E] text-white" style={{ fontFamily: "Manrope, ui-sans-serif, system-ui, sans-serif" }}>
      <nav className="border-b border-white/5 px-6 py-4">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <Link to="/" className="block" data-testid="funder-quiz-logo-home"><ClinqLogo /></Link>
          <Link to="/login" data-testid="funder-quiz-signin-link" className="text-sm font-semibold text-white/70 hover:text-white">
            Sign in
          </Link>
        </div>
      </nav>

      <div className="mx-auto max-w-3xl px-6 py-10 sm:py-16">
        <div className="flex items-center justify-center gap-2" data-testid="funder-quiz-progress">
          {[1, 2, 3, 4, 5, 6].map((s) => (
            <span key={s} className={"h-1.5 w-10 rounded-full transition-all " + (s <= step ? "bg-[#F59E0B]" : "bg-white/15")} />
          ))}
        </div>
        <p className="mt-3 text-center text-[10px] font-bold uppercase tracking-[0.18em] text-white/50">
          Step {step} of 6 · Funder Quiz
        </p>

        <section data-testid={`funder-quiz-step-${step}`} className="mt-10">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-400/15 ring-2 ring-emerald-400/30">
            <MetaIcon className="h-5 w-5 text-emerald-300" />
          </div>
          <h1
            className="mt-5 font-extrabold leading-[1.15] tracking-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]"
            style={{ fontFamily: "Manrope, sans-serif", fontSize: "clamp(1.5rem, 3.6vw, 2.5rem)" }}
          >
            {meta.title}
          </h1>
          <p className="mt-4 text-base text-white/70">{meta.sub}</p>

          {step === 1 && (
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {INVESTOR_TYPES.map(({ id, Icon, label, hint }) => {
                const active = investorType === id;
                return (
                  <button key={id} type="button"
                    data-testid={`funder-quiz-investor-${id}`}
                    onClick={() => setInvestorType(id)}
                    className={"flex flex-col items-start gap-3 rounded-2xl border p-5 text-left transition-all " +
                      (active
                        ? "border-[#F59E0B] bg-[#F59E0B]/10 ring-2 ring-[#F59E0B]/40"
                        : "border-white/10 bg-white/[0.03] hover:border-emerald-400/50 hover:bg-white/[0.06]")}>
                    <div className={"flex h-11 w-11 items-center justify-center rounded-xl " +
                      (active ? "bg-[#F59E0B]/20" : "bg-emerald-400/10")}>
                      <Icon className={"h-5 w-5 " + (active ? "text-[#F59E0B]" : "text-emerald-300")} />
                    </div>
                    <span className="text-base font-bold tracking-tight">{label}</span>
                    <span className="text-xs leading-relaxed text-white/55">{hint}</span>
                  </button>
                );
              })}
            </div>
          )}

          {step === 2 && (
            <>
              <div className="mt-8 grid gap-2.5 sm:grid-cols-2">
                {SECTORS.map((s) => (
                  <MultiCard key={s.id} opt={s} active={sectors.includes(s.id)}
                    onClick={() => toggle(setSectors)(s.id)} testid={`funder-quiz-sector-${s.id}`} />
                ))}
              </div>
              {sectors.includes("other") && (
                <input
                  data-testid="funder-quiz-sector-other-input"
                  type="text"
                  placeholder="Tell us which sector…"
                  value={sectorOther}
                  onChange={(e) => setSectorOther(e.target.value)}
                  className="mt-3 w-full rounded-xl border border-white/15 bg-white/[0.05] px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-emerald-400/60 focus:outline-none"
                />
              )}
            </>
          )}

          {step === 3 && (
            <div className="mt-8 grid gap-2.5">
              {TICKETS.map((t) => {
                const active = ticket === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    data-testid={`funder-quiz-ticket-${t.id}`}
                    onClick={() => setTicket(t.id)}
                    className={
                      "flex items-center gap-3 rounded-xl border px-4 py-3.5 text-left transition-all " +
                      (active
                        ? "border-[#F59E0B] bg-[#F59E0B]/10 ring-2 ring-[#F59E0B]/40"
                        : "border-white/10 bg-white/[0.03] hover:border-emerald-400/50 hover:bg-white/[0.06]")
                    }
                  >
                    <div className={
                      "flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 transition-all " +
                      (active ? "border-[#F59E0B] bg-[#F59E0B]" : "border-white/30 bg-transparent")
                    }>
                      {active && <CheckCircle2 className="h-4 w-4 text-[#04302E]" />}
                    </div>
                    <Coins className={"h-4 w-4 flex-shrink-0 " + (active ? "text-[#F59E0B]" : "text-emerald-300/70")} />
                    <span className="text-sm font-semibold tracking-tight">{t.label}</span>
                  </button>
                );
              })}
            </div>
          )}

          {step === 4 && (
            <div className="mt-8 grid gap-3 sm:grid-cols-3">
              {DEAL_TYPES.map(({ id, Icon, label, hint }) => {
                const active = dealFocus === id;
                return (
                  <button key={id} type="button"
                    data-testid={`funder-quiz-dealfocus-${id}`}
                    onClick={() => setDealFocus(id)}
                    className={"flex flex-col items-start gap-3 rounded-2xl border p-5 text-left transition-all " +
                      (active
                        ? "border-[#F59E0B] bg-[#F59E0B]/10 ring-2 ring-[#F59E0B]/40"
                        : "border-white/10 bg-white/[0.03] hover:border-emerald-400/50 hover:bg-white/[0.06]")}>
                    <div className={"flex h-11 w-11 items-center justify-center rounded-xl " +
                      (active ? "bg-[#F59E0B]/20" : "bg-emerald-400/10")}>
                      <Icon className={"h-5 w-5 " + (active ? "text-[#F59E0B]" : "text-emerald-300")} />
                    </div>
                    <span className="text-base font-bold tracking-tight">{label}</span>
                    <span className="text-xs leading-relaxed text-white/55 max-sm:line-clamp-2">{hint}</span>
                  </button>
                );
              })}
            </div>
          )}

          {step === 5 && (
            <div className="mt-8 grid gap-3 sm:grid-cols-3">
              {RISK_PROFILES.map(({ id, Icon, label, tag, hint }) => {
                const active = riskAppetite === id;
                return (
                  <button key={id} type="button"
                    data-testid={`funder-quiz-risk-${id}`}
                    onClick={() => { setRiskAppetite(id); setStructures(RISK_TO_STRUCTURES[id] || []); }}
                    className={"flex flex-col items-start gap-3 rounded-2xl border p-5 text-left transition-all " +
                      (active
                        ? "border-[#F59E0B] bg-[#F59E0B]/10 ring-2 ring-[#F59E0B]/40"
                        : "border-white/10 bg-white/[0.03] hover:border-emerald-400/50 hover:bg-white/[0.06]")}>
                    <div className={"flex h-11 w-11 items-center justify-center rounded-xl " +
                      (active ? "bg-[#F59E0B]/20" : "bg-emerald-400/10")}>
                      <Icon className={"h-5 w-5 " + (active ? "text-[#F59E0B]" : "text-emerald-300")} />
                    </div>
                    <span className="text-base font-bold tracking-tight">{label}</span>
                    <span className={"rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider " +
                      (active ? "bg-[#F59E0B]/20 text-[#F59E0B]" : "bg-emerald-400/10 text-emerald-300")}>{tag}</span>
                    <span className="text-xs leading-relaxed text-white/55">{hint}</span>
                  </button>
                );
              })}
            </div>
          )}

          {step === 6 && (
            <>
              <div className="mt-8 grid gap-2.5 sm:grid-cols-2">
                {GEOS.map((g) => (
                  <MultiCard key={g.id} opt={g} active={geos.includes(g.id)}
                    onClick={() => toggle(setGeos)(g.id)} testid={`funder-quiz-geo-${g.id}`} />
                ))}
              </div>
              {geos.includes("specific") && (
                <input
                  data-testid="funder-quiz-geo-specific"
                  type="text"
                  placeholder="e.g. Kenya, Nigeria, Ghana"
                  value={geoSpecific}
                  onChange={(e) => setGeoSpecific(e.target.value)}
                  className="mt-3 w-full rounded-xl border border-white/15 bg-white/[0.05] px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-emerald-400/60 focus:outline-none"
                />
              )}
            </>
          )}

          <div className="mt-10 flex items-center justify-between">
            {step > 1 ? (
              <button data-testid="funder-quiz-back" onClick={back}
                className="inline-flex items-center gap-1 text-sm font-semibold text-white/70 hover:text-white">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
            ) : <span />}
            <button
              data-testid="funder-quiz-next"
              disabled={!canNext}
              onClick={proceed}
              className={
                "inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold tracking-tight transition-all " +
                (canNext
                  ? (step === 6
                      ? "bg-[#F59E0B] text-[#04302E] shadow-lg shadow-black/30 hover:bg-amber-400 hover:shadow-xl"
                      : "bg-[#0A8B85] text-white shadow-lg shadow-black/30 ring-1 ring-emerald-300/0 hover:bg-[#066667] hover:shadow-xl hover:ring-emerald-300/60 hover:shadow-emerald-400/20")
                  : "cursor-not-allowed bg-white/10 text-white/40")
              }
            >
              {step === 6 ? "See Funder-Ready Opportunities" : "Next"}
              <ArrowRight className={"h-4 w-4 " + (step === 6 ? "" : "text-emerald-300")} />
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
